import type {Door, GridPoint, LevelGeometryDocument, LevelTransition, WallPath} from "@/dungeons/model/DungeonGeometry.ts";
import {validTransitionPolygon} from "@/dungeons/geometry/TransitionPolygon.ts";
import type {DoorEndpoint, SelectedGeometryFeature} from "./GeometryEditorTypes.ts";

export const pointsEqual = (first: GridPoint, second: GridPoint) =>
    first.x === second.x && first.y === second.y;

export const addWall = (document: LevelGeometryDocument, wall: WallPath): LevelGeometryDocument =>
    wall.points.length < 2 ? document : {...document, walls: [...document.walls, wall]};

export const addTransition = (document: LevelGeometryDocument, transition: LevelTransition): LevelGeometryDocument =>
    validTransitionPolygon(transition.points)
        ? {...document, transitions: [...document.transitions, transition]}
        : document;

export const moveTransitionPoint = (document: LevelGeometryDocument, id: string, index: number,
    point: GridPoint): LevelGeometryDocument => {
    const transition = document.transitions.find(item => item.id === id);
    if (!transition?.points[index] || pointsEqual(transition.points[index], point)) return document;
    const points = transition.points.map((current, i) => i === index ? point : current);
    return validTransitionPolygon(points) ? {...document, transitions: document.transitions.map(item =>
        item.id === id ? {...item, points} : item)} : document;
};

export const removeTransitionPoint = (document: LevelGeometryDocument, id: string,
    index: number): LevelGeometryDocument => {
    const transition = document.transitions.find(item => item.id === id);
    if (!transition?.points[index]) return document;
    const points = transition.points.filter((_, i) => i !== index);
    return validTransitionPolygon(points) ? {...document, transitions: document.transitions.map(item =>
        item.id === id ? {...item, points} : item)} : document;
};

export const updateTransition = (document: LevelGeometryDocument, id: string,
    changes: Partial<Pick<LevelTransition, "kind" | "label" | "targetLevelId" | "arrivalImagePosition">>):
    LevelGeometryDocument => {
    const transition = document.transitions.find(item => item.id === id);
    if (!transition) return document;
    const updated = {...transition, ...changes};
    if (JSON.stringify(updated) === JSON.stringify(transition)) return document;
    return {...document, transitions: document.transitions.map(item => item.id === id ? updated : item)};
};

export const moveWallPoint = (
    document: LevelGeometryDocument,
    wallId: string,
    pointIndex: number,
    point: GridPoint,
): LevelGeometryDocument => {
    const wall = document.walls.find(candidate => candidate.id === wallId);
    const current = wall?.points[pointIndex];
    if (!wall || !current || pointsEqual(current, point)) return document;
    const previous = wall.points[pointIndex - 1];
    const next = wall.points[pointIndex + 1];
    if ((previous && pointsEqual(previous, point)) || (next && pointsEqual(next, point))) return document;
    return {
        ...document,
        walls: document.walls.map(candidate => candidate.id === wallId
            ? {...candidate, points: candidate.points.map((value, index) => index === pointIndex ? point : value)}
            : candidate),
    };
};

export const removeWallPoint = (
    document: LevelGeometryDocument,
    wallId: string,
    pointIndex: number,
): LevelGeometryDocument => {
    const wall = document.walls.find(candidate => candidate.id === wallId);
    if (!wall || wall.points.length <= 2 || !wall.points[pointIndex]) return document;
    return {
        ...document,
        walls: document.walls.map(candidate => candidate.id === wallId
            ? {...candidate, points: candidate.points.filter((_, index) => index !== pointIndex)}
            : candidate),
    };
};

export const addDoor = (document: LevelGeometryDocument, door: Door): LevelGeometryDocument =>
    pointsEqual(door.start, door.end) ? document : {...document, doors: [...document.doors, door]};

export const moveDoorPoint = (
    document: LevelGeometryDocument,
    doorId: string,
    endpoint: DoorEndpoint,
    point: GridPoint,
): LevelGeometryDocument => {
    const door = document.doors.find(candidate => candidate.id === doorId);
    if (!door || pointsEqual(door[endpoint], point)) return document;
    const otherEndpoint = endpoint === "start" ? door.end : door.start;
    if (pointsEqual(otherEndpoint, point)) return document;
    return {
        ...document,
        doors: document.doors.map(candidate => candidate.id === doorId
            ? {...candidate, [endpoint]: point}
            : candidate),
    };
};

export const updateDoor = (
    document: LevelGeometryDocument,
    doorId: string,
    changes: Partial<Pick<Door, "initialState" | "secret" | "label" | "notes">>,
): LevelGeometryDocument => {
    const door = document.doors.find(candidate => candidate.id === doorId);
    if (!door) return document;
    const updated = {...door, ...changes};
    if (updated.initialState === door.initialState && updated.secret === door.secret
        && updated.label === door.label && updated.notes === door.notes) return document;
    return {
        ...document,
        doors: document.doors.map(candidate => candidate.id === doorId ? updated : candidate),
    };
};

export const deleteFeature = (
    document: LevelGeometryDocument,
    selection: SelectedGeometryFeature,
): LevelGeometryDocument => {
    if (!featureExists(document, selection)) return document;
    switch (selection.type) {
        case "wall": return {...document, walls: document.walls.filter(wall => wall.id !== selection.id)};
        case "door": return {...document, doors: document.doors.filter(door => door.id !== selection.id)};
        case "transition": return {...document, transitions: document.transitions.filter(item => item.id !== selection.id)};
    }
};

export const featureExists = (document: LevelGeometryDocument, selection: SelectedGeometryFeature) => {
    switch (selection.type) {
        case "wall": return document.walls.some(wall => wall.id === selection.id);
        case "door": return document.doors.some(door => door.id === selection.id);
        case "transition": return document.transitions.some(item => item.id === selection.id);
    }
};
